import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { CircularProgress, Stack } from '@mui/material'
import { useHistory } from 'react-router-dom'
import { Topic, BoltRounded, Description } from '@mui/icons-material'

import { useStores } from '@trivie/core'
import { TopicApi, QuizApi } from '@trivie/core/src/services/api-objects'
import { WorkflowApi } from '@trivie/core/src/services/api-objects/WorkflowApi'
import { Panel } from '@trivie/ui-web/components/panel/panel'
import { Text } from '@trivie/ui-web/components/text'
import { color } from '@trivie/ui-web/theme'

const LearningPathIcon = () => (
  <svg width="20" height="20" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12.7668 14.5C12.3113 14.5 11.9141 14.3639 11.5752 14.0917C11.2363 13.8194 11.0224 13.4889 10.9335 13.1H7.3835C6.65016 13.1 6.04183 12.8583 5.5585 12.375C5.07516 11.8917 4.8335 11.2833 4.8335 10.55C4.8335 9.81667 5.07516 9.20833 5.5585 8.725C6.04183 8.24167 6.65016 8 7.3835 8H8.66683C9.12239 8 9.49461 7.85556 9.7835 7.56667C10.0724 7.27778 10.2168 6.90556 10.2168 6.45C10.2168 5.99444 10.0724 5.62222 9.7835 5.33333C9.49461 5.04444 9.12239 4.9 8.66683 4.9H5.06683C4.96683 5.28889 4.75016 5.61944 4.41683 5.89167C4.0835 6.16389 3.68905 6.3 3.2335 6.3C2.70016 6.3 2.25016 6.11667 1.8835 5.75C1.51683 5.38333 1.3335 4.93333 1.3335 4.4C1.3335 3.86667 1.51683 3.41667 1.8835 3.05C2.25016 2.68333 2.70016 2.5 3.2335 2.5C3.68905 2.5 4.0835 2.63611 4.41683 2.90833C4.75016 3.18056 4.96683 3.51111 5.06683 3.9H8.66683C9.40016 3.9 10.0085 4.14167 10.4918 4.625C10.9752 5.10833 11.2168 5.71667 11.2168 6.45C11.2168 7.18333 10.9752 7.79167 10.4918 8.275C10.0085 8.75833 9.40016 9 8.66683 9H7.3835C6.92794 9 6.55572 9.14444 6.26683 9.43333C5.97794 9.72222 5.8335 10.0944 5.8335 10.55C5.8335 11.0056 5.97794 11.3778 6.26683 11.6667C6.55572 11.9556 6.92794 12.1 7.3835 12.1H10.9335C11.0335 11.7111 11.2502 11.3806 11.5835 11.1083C11.9168 10.8361 12.3113 10.7 12.7668 10.7C13.3002 10.7 13.7502 10.8833 14.1168 11.25C14.4835 11.6167 14.6668 12.0667 14.6668 12.6C14.6668 13.1333 14.4835 13.5833 14.1168 13.95C13.7502 14.3167 13.3002 14.5 12.7668 14.5ZM3.2335 5.3C3.48905 5.3 3.70294 5.21389 3.87516 5.04167C4.04739 4.86944 4.1335 4.65556 4.1335 4.4C4.1335 4.14444 4.04739 3.93056 3.87516 3.75833C3.70294 3.58611 3.48905 3.5 3.2335 3.5C2.97794 3.5 2.76405 3.58611 2.59183 3.75833C2.41961 3.93056 2.3335 4.14444 2.3335 4.4C2.3335 4.65556 2.41961 4.86944 2.59183 5.04167C2.76405 5.21389 2.97794 5.3 3.2335 5.3Z"
      fill="black"
      fillOpacity="0.7"
    />
  </svg>
)

const contentIcon: Record<string, React.ReactNode> = {
  topics: <Topic style={{ fontSize: 20 }} htmlColor={color.shade70} />,
  quizzes: <BoltRounded style={{ fontSize: 20 }} htmlColor={color.shade70} />,
  sources: <Description style={{ fontSize: 20 }} htmlColor={color.shade70} />,
  'learning paths': <LearningPathIcon />,
}

const typeMap: Record<string, string> = {
  topic: 'topics',
  topics: 'topics',
  quiz: 'quizzes',
  path: 'learning paths',
  Topic: 'topics',
  Topics: 'topics',
  Quiz: 'quizzes',
  Path: 'learning paths',
}

const groups = [
  {
    key: 'learning paths',
    title: 'Learning Paths',
    route: (id: number) => `/content/paths/${id}/overview`,
  },
  { key: 'topics', title: 'Topics', route: (id: number) => `/content/topics/${id}/overview` },
  { key: 'quizzes', title: 'Quizzes', route: (id: number) => `/content/quizzes/${id}/overview` },
  { key: 'sources', title: 'Sources', route: () => '' },
]

export const Content = observer(({ content, label }: { content: any[]; label?: string }) => {
  const history = useHistory()
  const [loading, setLoading] = useState(true)
  const [grouped, setGrouped] = useState<Record<string, any[]>>({})

  useEffect(() => {
    if (!content?.length) {
      setLoading(false)
      return
    }

    const mappedContent = content.map((c: any) => ({
      id: Number(c.id || c.object_id),
      type: typeMap[c.type || c.object_type] ?? (c.type || c.object_type),
    }))

    Promise.all([
      TopicApi.getAllTopics(
        'topics',
        false,
        [{ field: 'topic_title' }, { field: 'quiz_count' }],
        'list_topics',
      ),
      QuizApi.getAllQuizzes(
        'quizzes',
        false,
        [{ field: 'quiz_title' }, { field: 'quiz_question_count' }],
        'list_quizzes',
      ),
      WorkflowApi.getAssignmentPathDetails(),
    ])
      .then(([topics, quizzes, pathDetails]) => {
        if (!topics.ok || !quizzes.ok || !pathDetails.ok) return

        const topicData = topics.data.rows.map((topic: any) => {
          const title = topic.columns.find((c: any) => c.field === 'topic_title')
          const quizCount = topic.columns.find((c: any) => c.field === 'quiz_count')
          const count = quizCount?.value ?? 0
          return {
            id: topic.id,
            type: 'topics',
            label: title?.value ?? '',
            subLabel: `${count} ${count === 1 ? 'Quiz' : 'Quizzes'}`,
          }
        })

        const quizData = quizzes.data.rows.map((quiz: any) => {
          const title = quiz.columns.find((c: any) => c.field === 'quiz_title')
          const questionCount = quiz.columns.find((c: any) => c.field === 'quiz_question_count')
          const count = questionCount?.value ?? 0
          return {
            id: quiz.id,
            type: 'quizzes',
            label: title?.value ?? '',
            subLabel: `${count} ${count === 1 ? 'Question' : 'Questions'}`,
          }
        })

        const pathData =
          pathDetails.data?.paths.map((path: any) => {
            const t = path.topics?.length ?? 0
            const q = path.quizzes?.length ?? 0
            const s = path.sources?.length ?? 0
            const parts: string[] = []
            if (t > 0) parts.push(`${t} ${t === 1 ? 'Topic' : 'Topics'}`)
            if (q > 0) parts.push(`${q} ${q === 1 ? 'Quiz' : 'Quizzes'}`)
            if (s > 0) parts.push(`${s} ${s === 1 ? 'Source' : 'Sources'}`)
            return {
              id: path.path_id,
              type: 'learning paths',
              label: path.title,
              subLabel: parts.join(', '),
              sources: (path.sources ?? []).map((src: any) => ({
                id: src.id ?? src.source_id ?? src.resource_id,
                type: 'sources',
                label: src.title ?? src.file_name ?? '',
                subLabel: src.file_name ?? '',
              })),
            }
          }) ?? []

        const allContent = [...topicData, ...quizData, ...pathData]

        const matched = allContent.filter((item: any) =>
          mappedContent.some((pc: any) => pc.id === item.id && pc.type === item.type),
        )

        const result: Record<string, any[]> = {}
        matched.forEach((item: any) => {
          if (!result[item.type]) result[item.type] = []
          result[item.type].push(item)
        })

        setGrouped(result)
      })
      .finally(() => setLoading(false))
  }, [content])

  if (loading) {
    return (
      <div
        style={{
          flex: 1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: 32,
          minHeight: 200,
        }}
      >
        <CircularProgress />
      </div>
    )
  }

  const hasContent = Object.keys(grouped).length > 0

  return (
    <Stack gap={2} pb="40px">
      <Panel title={label ?? 'Included Content'} fill>
        {!hasContent ? (
          <Text variant="body1" text="No content" sx={{ color: 'text.secondary' }} />
        ) : (
          <Stack mt={2} gap={2}>
            {groups.map((group, index) => {
              const items = grouped[group.key]
              if (!items?.length) return null
              return (
                <Stack key={`${group.key}_${index}`} gap={2}>
                  <Text variant="body1Emphasized" text={group.title} />
                  <Stack gap={0.5} pl={2}>
                    {items.map((item: any, index2: number) => {
                      const route = group.route(item.id)
                      return (
                        <Stack
                          key={`${item.type}-${item.id}-${index2}`}
                          direction="row"
                          alignItems="center"
                          gap={1.5}
                          sx={{
                            p: 1,
                            borderRadius: 2,
                            ...(route
                              ? {
                                  cursor: 'pointer',
                                  '&:hover': { backgroundColor: 'action.hover' },
                                }
                              : {}),
                          }}
                          onClick={route ? () => history.push(route) : undefined}
                        >
                          {contentIcon[item.type] ?? null}
                          <Stack>
                            <Text variant="body1Medium" text={item.label} />
                            {item.subLabel && (
                              <Text
                                variant="body2"
                                text={item.subLabel}
                                sx={{ color: 'text.secondary' }}
                              />
                            )}
                          </Stack>
                        </Stack>
                      )
                    })}
                  </Stack>
                </Stack>
              )
            })}
          </Stack>
        )}
      </Panel>
    </Stack>
  )
})

const ProductContent = observer(({ id }: { id: string }) => {
  const { subscriptionStore } = useStores()
  const product = subscriptionStore.selectedProduct
  return <Content content={product?.content ?? []} />
})

export const SubscriptionProductContentTab = (props: any) => {
  const { id } = props
  return {
    name: 'Content',
    path: `/activities/subscriptions/${id}/content`,
    component: () => <ProductContent id={id} />,
  }
}
